import { motion, type MotionValue, useTransform } from "motion/react";
import { cn } from "@/lib/utils";

interface SegmentedProgressProps {
  activeIndex: number;
  className?: string;
  count: number;
  label: string;
  progress: MotionValue<number>;
}

interface ProgressSegmentProps {
  count: number;
  index: number;
  progress: MotionValue<number>;
}

function ProgressSegment({ count, index, progress }: ProgressSegmentProps) {
  const segmentProgress = useTransform(
    progress,
    [index / count, (index + 1) / count],
    [0, 1],
    { clamp: true }
  );
  const backgroundPosition =
    count === 1 ? "0% 0%" : `${(index / (count - 1)) * 100}% 0%`;

  return (
    <span
      data-progress-segment={index}
      className="relative min-w-0 flex-1 overflow-hidden rounded-full bg-white/15"
    >
      <motion.span
        className="absolute inset-0 origin-left bg-no-repeat"
        style={{
          scaleX: segmentProgress,
          backgroundImage:
            "linear-gradient(to right, #149CFF 0%, #DEFF77 100%)",
          backgroundPosition,
          backgroundSize: `${count * 100}% 100%`,
        }}
      />
    </span>
  );
}

export default function SegmentedProgress({
  activeIndex,
  className,
  count,
  label,
  progress,
}: SegmentedProgressProps) {
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={1}
      aria-valuemax={count}
      aria-valuenow={activeIndex + 1}
      className={cn("flex h-1 flex-1 gap-2", className)}
    >
      {Array.from({ length: count }, (_, index) => (
        <ProgressSegment
          key={index}
          count={count}
          index={index}
          progress={progress}
        />
      ))}
    </div>
  );
}
